import { CoreUtils, Formats, getWeekdayFromMomentISOIndex, getWeekdayNext } from "..";
import moment from "moment-timezone";
export const RestaurantUtils = {
    store: {
        url: (opts) => {
            if (opts.isTest) {
                return "http://localhost:3000";
            }
            return opts.domain ? `https://${opts.domain}` : `https://${opts.subdomain}.${opts.storeHost}`;
        },
    },
    opening_hours: {
        getOpenDays(hours) {
            return hours.map(({ day }) => day);
        },
        isTimeWithin({ dt, hours, special_hours, first_offset, last_offset, timezone, log, }) {
            if (log) {
                console.log("RUN IS TIME WITHIN");
            }
            let matchIndex = -1;
            let isWithin = false;
            const weekDate = dt.format(Formats.moment.date);
            const weekday = dt.clone().locale("en").format("dddd");
            const filtered_special_hours = (special_hours || [])
                .map((h, i) => (Object.assign(Object.assign({}, h), { index: i })))
                .filter((h) => h.date === weekDate);
            const isSpecialHours = filtered_special_hours.length > 0;
            const opening_hours = isSpecialHours
                ? filtered_special_hours
                : hours.map((h, i) => (Object.assign(Object.assign({}, h), { index: i }))).filter((h) => h.day === weekday);
            for (const slot of opening_hours) {
                // @ts-ignore
                if (isSpecialHours && slot.opened === false) {
                    continue;
                }
                if (slot.h24) {
                    isWithin = true;
                    matchIndex = slot.index;
                    break;
                }
                const date = dt.format(Formats.moment.date);
                const open = moment
                    .tz(`${date} ${slot.open}`, Formats.moment.datetime, timezone)
                    .add(first_offset, "minutes");
                const close = moment
                    .tz(`${date} ${slot.close}`, Formats.moment.datetime, timezone)
                    .subtract(last_offset, "minutes");
                if (log) {
                    console.log(`\nCurrent: ${dt.format(Formats.moment.datetime)}\nIs after open: ${open.format(Formats.moment.datetime)}\nIs before close: ${close.format(Formats.moment.datetime)}`);
                }
                if (dt.isSameOrAfter(open) && dt.isSameOrBefore(close)) {
                    // IF CURRENT BETWEEN OPEN AND CLOSE
                    isWithin = true;
                    matchIndex = slot.index;
                    break;
                }
            }
            return { isWithin, isSpecialHours, index: matchIndex };
        },
        closesIn({ dt, hours, special_hours, first_offset, last_offset, timezone, }) {
            const { isWithin, index, isSpecialHours } = this.isTimeWithin({
                dt,
                hours,
                special_hours,
                first_offset,
                last_offset,
                timezone,
            });
            if (!isWithin)
                return null;
            const slot = isSpecialHours ? special_hours[index] : hours[index];
            let continuesToNextDay = false;
            let nextSlot = null;
            if (slot.close === "23:59" || slot.h24) {
                const nextWeekday = getWeekdayNext(slot.day);
                const nextDayTimes = hours.filter((s) => s.day === nextWeekday);
                for (const s of nextDayTimes) {
                    if (s.h24 || s.open === "00:00") {
                        continuesToNextDay = true;
                        nextSlot = s;
                        break;
                    }
                }
            }
            const parsed = moment
                .tz(`${dt.format(Formats.moment.date)} ${slot.close}`, Formats.moment.datetime, timezone)
                .subtract(last_offset, "minutes");
            if (continuesToNextDay && nextSlot) {
                parsed.add(1, "day");
                parsed.set("hour", parseInt(nextSlot.close.split(":")[0], 10));
                parsed.set("minute", parseInt(nextSlot.close.split(":")[1], 10));
                parsed.subtract(last_offset, "minutes");
            }
            console.log("CLOSES AT", parsed.format(Formats.moment.datetime));
            const closesInMinutes = Math.round(parsed.diff(dt, "minutes"));
            const closesInMillis = Math.round(parsed.diff(dt, "milliseconds"));
            return closesInMinutes >= 0 && closesInMinutes <= 30
                ? {
                    minutes: closesInMinutes,
                    millis: closesInMillis,
                }
                : null;
        },
        isOpenToday({ hours, special_hours }) {
            special_hours = special_hours || [];
            const today = moment.tz(Date.now(), "Pacific/Auckland");
            const formattedDate = today.format(Formats.moment.date);
            let specialClosedToday = false;
            for (let i = 0; i < special_hours.length; i++) {
                if (special_hours[i].date === formattedDate && !special_hours[i].opened) {
                    specialClosedToday = true;
                    break;
                }
            }
            if (specialClosedToday) {
                return false;
            }
            const weekday = getWeekdayFromMomentISOIndex(today.isoWeekday());
            const openDays = hours.map(({ day }) => day);
            const hoursClosedToday = openDays.indexOf(weekday) === -1;
            return !hoursClosedToday;
        },
    },
    theme: {
        fromRestaurant(r) {
            const headerFontFamily = r.website.font_heading.split(":")[0];
            const bodyFontFamily = r.website.font_body.split(":")[0];
            return {
                font: {
                    header: `'${headerFontFamily}', sans-serif;`,
                    body: `'${bodyFontFamily}',Verdana,Helvetica, sans-serif;`,
                    size: 16,
                },
                colors: {
                    primary: r.website.color_primary,
                    primary_text: r.website.color_primary_text,
                    text: r.website.color_text,
                    background: r.website.color_background,
                    border: CoreUtils.colors.shadeAlternate(0.16, r.website.color_background),
                    success: "#51a351",
                    warning: "#f89406",
                    error: "#bd362f",
                },
                status_colors: {
                    complete: "#51A351",
                    onroute: "#00BFFF",
                    ready: "#0080FF",
                    confirmed: "#B7D024",
                    unconfirmed: "#efbb1e",
                    cancelled: "#BD362F",
                },
                input: {
                    height: 38,
                    fontSize: 16,
                },
                breakpoints: CoreUtils.ui.breakpoints,
            };
        },
    },
    menu: {
        getTree(menus) {
            return menus
                .filter((m) => m.categories.length)
                .map((menu) => ({
                value: menu.id,
                label: menu.name,
                children: menu.categories
                    .filter((c) => c.dishes.length)
                    .map((category) => ({
                    value: category.id,
                    label: category.name,
                    children: category.dishes.map((dish) => ({
                        value: dish.id,
                        label: dish.name,
                    })),
                })),
            }))
                .filter((node) => node.children.length);
        },
        getTreeFilter(menus, filter) {
            return menus
                .filter((m) => m.categories.length)
                .map((menu) => ({
                value: menu.id,
                label: menu.name,
                children: menu.categories
                    .filter((c) => c.dishes.length)
                    .map((category) => ({
                    value: category.id,
                    label: category.name,
                    children: category.dishes.filter(filter).map((dish) => ({
                        value: dish.id,
                        label: dish.name,
                    })),
                })),
            }))
                .filter((node) => node.children.length);
        },
        findCategoryById(menus, category_id) {
            for (let menuIndex = 0; menuIndex < menus.length; menuIndex++) {
                const menu = menus[menuIndex];
                for (let categoryIndex = 0; categoryIndex < menu.categories.length; categoryIndex++) {
                    const category = menu.categories[categoryIndex];
                    if (category.id === category_id) {
                        return { menuIndex, categoryIndex };
                    }
                }
            }
            return null;
        },
        findDish(menus, dish_id) {
            for (const menu of menus) {
                for (const category of menu.categories) {
                    for (const dish of category.dishes) {
                        if (dish.id === dish_id) {
                            return { dish, category, menu };
                        }
                    }
                }
            }
            return null;
        },
    },
    dish: {
        optionSetValidate(os, options) {
            const setOptions = options.filter((o) => o.option_set_id === os.id);
            let ok = false;
            const total = setOptions.reduce((t, o) => t + o.quantity, 0);
            if (os.is_required) {
                if (os.is_multi) {
                    if (os.max_options) {
                        if ((os.min_options || 1) <= total && total <= os.max_options) {
                            ok = true;
                        }
                    }
                    else if (total >= (os.min_options || 1)) {
                        ok = true;
                    }
                }
                else if (total === 1) {
                    ok = true;
                }
            }
            // tslint:disable-next-line
            else {
                if (os.max_options) {
                    if (total <= os.max_options) {
                        ok = true;
                    }
                }
                else {
                    ok = true;
                }
            }
            return { error: !ok };
        },
    },
    cart: {},
    order: {
        color(status) {
            switch (status) {
                case "unconfirmed":
                    return "#efbb1e";
                case "confirmed":
                    return "#B7D024";
                case "ready":
                    return "#0080FF";
                case "on_route":
                    return "#00BFFF";
                case "complete":
                    return "#51A351";
                case "cancelled":
                    return "#BD362F";
                default:
                    return "#efbb1e";
            }
        },
        payment: {
            poli: {
                statusFromTransaction: (TransactionStatusCode) => {
                    return TransactionStatusCode === "Completed"
                        ? "success"
                        : TransactionStatusCode === "Cancelled"
                            ? "error"
                            : TransactionStatusCode === "Failed"
                                ? "error"
                                : TransactionStatusCode === "ReceiptUnverified"
                                    ? "error"
                                    : TransactionStatusCode === "TimedOut"
                                        ? "error"
                                        : "pending";
                },
            },
        },
    },
};
export function restaurantGetService(r, service) {
    if (service === "pickup")
        return r.service_pickup;
    else if (service === "delivery")
        return r.service_delivery;
    else if (service === "dine_in")
        return r.service_dine_in;
    else if (service === "table_booking")
        return r.service_table_booking;
    throw new Error(`Calling restaurantGetService with invalid service ${service}`);
}
export function restaurantGetPayment(r, method) {
    if (method === "cash")
        return r.payment_cash;
    else if (method === "card")
        return r.payment_card;
    else if (method === "voucher")
        return r.payment_voucher;
    else if (method === "stripe")
        return r.payment_stripe;
    else if (method === "bambora")
        return r.payment_bambora;
    else if (method === "poli")
        return r.payment_poli;
    else if (method === "paymark_eftpos")
        return r.payment_paymark_eftpos;
    else if (method === "windcave")
        return r.payment_windcave;
    else if (method === "custom")
        return r.payment_custom;
    throw new Error(`Calling restaurantGetPayment with invalid payment ${method}`);
}
export function restaurantGetPaymentIncCustom(r, payment) {
    const { type, type_id } = payment;
    if (type === "cash")
        return r.payment_cash;
    else if (type === "card")
        return r.payment_card;
    else if (type === "voucher")
        return r.payment_voucher;
    else if (type === "stripe")
        return r.payment_stripe;
    else if (type === "bambora")
        return r.payment_bambora;
    else if (type === "poli")
        return r.payment_poli;
    else if (type === "paymark_eftpos")
        return r.payment_paymark_eftpos;
    else if (type === "windcave")
        return r.payment_windcave;
    else if (type === "custom") {
        return ((r.payment_custom || []).find((p) => p.id === type_id) || {
            id: "unknown",
            label: "Custom",
            label_delivery: "",
            label_print: "",
            xero_clearing_id: "",
            enabled: false,
            enable_online: false,
            enable_pos: false,
            max_order: 0,
            restrict_services: [],
        });
    }
    throw new Error(`Calling restaurantGetPayment with invalid payment ${type}`);
}
//# sourceMappingURL=index.js.map